import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { TiktokClientPayload } from '@/pages/api/tracking/tiktok/types';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { useTikTokClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerTikTokConversionEvent } from 'presentation/events/services/tiktokService';
import { getReferrerForTracking, safeDecodeUrl } from 'presentation/events/utils/ga4Utils';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { useGetCategories } from 'shared/custom/hooks/Categories';

import { SendMediaEventProps } from './types';
import { calculateProductsValue, getViewItemProductData } from './utils';

export const useTrackViewItemTikTok = () => {
	const { settings } = useSettings();
	const { navigation } = useGetCategories();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { ttclid, ttp } = useTikTokClickIdentifiers();

	const { userData, storeId } = settings;

	const sendTikTokEvent = useCallback(
		({ eventId, products, personInfo }: SendMediaEventProps) => {
			if (userData?.ENABLE_TIKTOK_CALL !== 'true') return;
			if (!physicalStoreId) return;

			const contentIds = products.map((product) => getItemPartNumber(product));

			const contents = products.map((product) => {
				const {
					id: content_id,
					price,
					quantity,
					categoryString: content_category,
				} = getViewItemProductData(product, physicalStoreId, navigation);
				return {
					content_id,
					content_name: product.name,
					content_category,
					brand: product.manufacturer,
					price,
					quantity,
				};
			});

			const totalQuantity = contents.reduce((sum, item) => sum + item.quantity, 0);
			const value = calculateProductsValue(products, physicalStoreId);

			const tiktokPayload: TiktokClientPayload = {
				event: 'ViewContent',
				event_id: eventId,
				properties: {
					content_ids: contentIds,
					quantity: totalQuantity,
					num_items: products.length,
					value: Number(value.toFixed(2)),
					currency: DEFAULT_CURRENCY,
					content_type: 'product',
					contents,
				},
				user: {
					email: [personInfo?.email ?? ''],
					first_name: personInfo?.firstName,
					last_name: personInfo?.lastName,
					phone: [personInfo?.phone ?? ''],
					city: storeLocation?.city,
					state: storeLocation?.state,
					country: 'mx',
					external_id: [getExternalId()],
					locale: 'es-MX',
					zip_code: storeLocation?.zipCode,
					ttp,
					ttclid,
				},
				page: {
					url: safeDecodeUrl(location.href),
					referrer: getReferrerForTracking(),
				},
				storeId,
				test_event_code: userData?.TIKTOK_TEST_CODE,
			};

			triggerTikTokConversionEvent(tiktokPayload);
		},
		[
			userData?.ENABLE_TIKTOK_CALL,
			userData?.TIKTOK_TEST_CODE,
			physicalStoreId,
			storeLocation,
			navigation,
			ttp,
			ttclid,
			storeId,
		]
	);

	return { sendTikTokEvent };
};
